import {
  styled,
  CardMedia,
  Card,
  Paper,
  Typography,
  CardActions,
  CardContent,
  IconButton,
  CircularProgress,
  Box,
  FormHelperText,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { IWithClassName } from "../../theme/default.theme";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  Star,
  CancelRounded,
  CheckBoxOutlineBlank,
  CheckBox,
} from "@mui/icons-material";
import { AppType } from "../../enum/app";
import { IAppInfo } from "../../interfaces/dto/app.dto";
import DialogBox from "./delete-app-dialog";
import { getAppsThunk } from "../../redux/thunks/app.thunk";

interface IProps extends IWithClassName {
  deleteHandler?: (id: string) => void;
  selectHandler?: (app: IAppInfo) => void;
  value?: IAppInfo;
  error?: string;
}

const AppTiles = ({
  deleteHandler,
  className,
  selectHandler,
  value,
  error,
}: IProps) => {
  const dispatch = useAppDispatch();
  const { apps } = useAppSelector((state) => state.app);
  const [deleteAppId, setDeleteAppId] = useState<string | undefined>();
  const [selected, setSelected] = useState<IAppInfo>();

  useEffect(() => {
    setSelected(value);
  }, [value]);

  useEffect(() => {
    dispatch(getAppsThunk());
  }, [dispatch]);

  const appSelectHandler = (app: IAppInfo) => {
    setSelected(app);
    selectHandler && selectHandler(app);
  };

  const deleteApp = () => {
    deleteAppId && deleteHandler && deleteHandler(deleteAppId);
    closeDialog();
  };

  const closeDialog = () => setDeleteAppId(undefined);

  return (
    <Box className={className}>
      <DialogBox
        open={!!deleteAppId}
        deleteApp={deleteApp}
        closeDialog={closeDialog}
      />
      {apps.data ? (
        apps.data.map((app) => (
          <Card
            className="app-tile"
            component={Paper}
            key={app.id}
            onClick={() => appSelectHandler(app)}
          >
            <CardMedia
              component="img"
              className="app-thumbnail"
              image={app.thumbnail}
            />
            <CardContent>
              <Typography variant="h6">{app.title}</Typography>
              <Typography color="text.secondary">{app.author}</Typography>
              <Typography className="rating-container">
                {app.rating} <Star className="start-icon" />
                <img
                  src={`/assets/images/${
                    app.type === AppType.Android ? "play_store" : "apple_store"
                  }.png`}
                  alt="play_store"
                  height="25"
                />
              </Typography>
            </CardContent>

            <CardActions>
              {selectHandler && (
                <AppCheckbocIcon
                  selectedId={selected?.id ?? ""}
                  appId={app.id}
                />
              )}
              {deleteHandler && (
                <IconButton
                  onClick={() => setDeleteAppId(app.id)}
                  className="delete-button"
                >
                  <CancelRounded />
                </IconButton>
              )}
            </CardActions>
          </Card>
        ))
      ) : (
        <CircularProgress className="loader" />
      )}
      <FormHelperText error={!!error}>{error}</FormHelperText>
    </Box>
  );
};

interface IAppCheckbocIcon {
  selectedId: string;
  appId: string;
}

const AppCheckbocIcon = ({ selectedId, appId }: IAppCheckbocIcon) => (
  <>
    {selectedId === appId ? (
      <CheckBox className="app-checkbox" />
    ) : (
      <CheckBoxOutlineBlank className="app-checkbox" />
    )}
  </>
);

export default styled(AppTiles)(({ theme }) => ({
  padding: theme.spacing(2),
  cursor: "pointer",
  display: "flex",
  gap: theme.spacing(2),
  flexWrap: "wrap",
  width: "100%",
  ".loader": {
    margin: "auto",
  },
  ".delete-button": {
    padding: 0,
    margin: "auto",
  },
  ".rating-container": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.4),
    img: {
      height: 25,
      margin: theme.spacing(0, 0.5),
    },
  },
  ".app-tile": {
    display: "grid",
    gridTemplateColumns: "100px 1fr 40px",
    ".MuiCardMedia-root": {
      width: 75,
      margin: "auto",
    },
    "MuiCardContent-root:last-child": {
      padding: theme.spacing(2),
    },
    ".start-icon": {
      fontSize: 12,
    },
    h6: {
      fontWeight: 600,
    },
  },
  ".app-tile-checked": {
    borderColor: theme.palette.primary.main,
  },
  ".app-thumbnail": {
    width: 151,
  },
  ".app-checkbox": {
    position: "absolute",
  },
  ".MuiCardActions-root": {
    alignItems: "flex-start",
  },
}));
